// src/oura/components/search/SearchMovement.tsx
//
// Which searches changed place on Google against the window before, and
// which sit just off page one. Both read the same Search Console rows as the
// tables below them, so they are subsets of what the table lists, not new
// measurements.

import { useMemo } from "react";
import { TargetIcon, TrendingUpIcon } from "lucide-react";
import type { AeoSearchKeyword } from "@service/aeo";
import { FoldCard, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { TABLE } from "../client/staffScreen";
import { isCloseToPageOne, num, position } from "./format";

/**
 * Google's position is an average over the window, so the move is a decimal
 * (3.5). Said in whole places, the way a client counts them (Jack, 26 Oct).
 */
function placesMoved(delta: number): string {
    const places = Math.round(Math.abs(delta));
    if (places < 1) return "less than 1 place";
    return places === 1 ? "1 place" : `${places} places`;
}

/** The six searches that moved most, either way. */
export function MoversCard({ keywords, isStaff }: { keywords: AeoSearchKeyword[]; isStaff?: boolean }) {
    const moved = useMemo(
        () => keywords
            .filter((k) => k.positionDelta !== null && k.positionDelta !== 0)
            .sort((a, b) => Math.abs(b.positionDelta!) - Math.abs(a.positionDelta!))
            .slice(0, 6),
        [keywords],
    );
    return (
        <FoldCard
            id="search-movers"
            title="Searches that moved"
            icon={{ icon: TrendingUpIcon, area: "google" }}
            flush
            foldedFact={moved[0] ? `Moved most: ${moved[0].query}` : undefined}
        >
            <p className={cn(TYPE.tc, "m-0 px-5 pb-3 text-gray-500")}>Position on Google now, against the period before</p>
            {moved.length === 0 ? (
                // Null deltas are a first window: nothing to compare, not "no movement".
                <p className={cn(TYPE.tb, "m-0 px-5 pb-5 text-gray-500")}>
                    {keywords.some((k) => k.positionDelta !== null) ? "No search changed place." : "No earlier period to compare."}
                </p>
            ) : (
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        <col />
                        <col className="w-48" />
                        {isStaff && <col className="w-20" />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th className={cn(TABLE.th, "text-left")}>Search</th>
                            <th className={cn(TABLE.th, "text-right")}>Change</th>
                            {isStaff && <th className={cn(TABLE.th, "text-right")}>Clicks</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {moved.map((k) => {
                            // NEGATIVE is an improvement. Said in words, "Up 3.5": an up arrow
                            // beside a smaller number read as going the wrong way (audit, 25 Oct).
                            const up = k.positionDelta! < 0;
                            return (
                                <tr key={k.query}>
                                    <td className={cn(TABLE.td, TYPE.tb, "py-2.5 break-words text-gray-900")}>{k.query}</td>
                                    <td className={cn(TABLE.td, TYPE.tc, "text-right tabular-nums")}>
                                        <span className={cn("font-medium", up ? "text-emerald-700" : "text-rose-700")}>
                                            {up ? "Up" : "Down"} {placesMoved(k.positionDelta!)}
                                        </span>{" "}
                                        <span className="whitespace-nowrap text-gray-500">· now #{Math.round(k.position)}</span>
                                    </td>
                                    {isStaff && <td className={cn(TABLE.td, TYPE.tc, "text-right tabular-nums text-gray-500")}>{num(k.clicks)}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </FoldCard>
    );
}

/** Searches for what the business sells that sit on page two, most shown first. */
export function CloseToPageOneCard({ keywords, isStaff }: { keywords: AeoSearchKeyword[]; isStaff?: boolean }) {
    const close = useMemo(
        () => keywords.filter(isCloseToPageOne).sort((a, b) => b.impressions - a.impressions).slice(0, 4),
        [keywords],
    );
    return (
        <FoldCard
            id="close-to-page-one"
            title="Close to page one"
            icon={{ icon: TargetIcon, area: "google" }}
            foldedFact={close.length ? `${close.length} just off page one` : undefined}
        >
            <p className={cn(TYPE.tc, "m-0 -mt-1 mb-3 text-gray-500")}>
                {isStaff ? "Non-brand searches at position 11 to 20, most shown first." : "Searches you're nearly on page one of Google for."}
            </p>
            {close.length === 0 ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>None of these searches sit just off page one.</p>
            ) : (
                <div className="flex flex-col gap-2">
                    {close.map((k) => (
                        <div key={k.query} className="flex flex-col rounded-xl bg-gray-50 px-3.5 py-2.5">
                            <span className={cn(TYPE.tb, "break-words font-semibold text-gray-900")}>{k.query}</span>
                            <span className={cn(TYPE.tc, "text-gray-500")}>Position {position(k.position)} · shown {num(k.impressions)} times</span>
                        </div>
                    ))}
                </div>
            )}
        </FoldCard>
    );
}
